import { FC } from 'react';

import Link from 'next/link';
import router from 'next/router';

import { Collapse } from '@mui/material';

import { IoIosArrowBack } from 'react-icons/io';
import { IoMenuSharp } from 'react-icons/io5';
import { MdClose } from 'react-icons/md';
import { useQuery } from 'react-query';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useDashboardData } from '@hooks_iqfu/dashboard/useDashboardData';

import { SideMenuArrow } from '@components_iqfu//icons/SideMenuArrow';

import { adminLoginUserDataAtom, adminUserRoleSelector } from '@recoil_iqfu/atoms/auth/adminLoginUserData';
import { showSideMenuAtom } from '@recoil_iqfu/atoms/side_menu/showSideMenu';

import { availableMenuList, outsourcerAvailableMenuList, OutsourcerPermissionCodes } from '@constants_common';
import { TargetMenu, useMenuToggle } from '@hooks_common/useMenuToggle';

import styles from './SideMenu.module.scss';

type Props = {
  toggleSpMenu: () => void;
  spMenuOpen: boolean;
};

const AdminSideMenu: FC<Props> = ({ toggleSpMenu, spMenuOpen }) => {
  const [showSideMenu, setShowSideMenu] = useRecoilState(showSideMenuAtom);
  const adminLoginUserData = useRecoilValue(adminLoginUserDataAtom);
  const adminUserRole = useRecoilValue(adminUserRoleSelector);

  const { getAdminDashboardData } = useDashboardData();
  const iqfuServiceAccessibleRole = ['master', 'manager', 'member', 'sales_member'];
  const { data: dashboardData } = useQuery(['getAdminDashboardData'], () => getAdminDashboardData(), {
    enabled: iqfuServiceAccessibleRole.includes(String(adminUserRole)),
  });

  const {
    iqfuMenuToggleState: { openIqfu, openChat, openCsTool, openLeaseback, openDeal, openJusetsu, openOthers },
    iqfuMenuToggleHandler,
  } = useMenuToggle();

  const needsSection = (menuName: string): boolean => {
    let result = false;
    if (adminUserRole && adminUserRole !== 'outsourcer') {
      result = availableMenuList[adminUserRole].includes(menuName);
    } else if (adminUserRole === 'outsourcer' && adminLoginUserData?.permission_codes) {
      result = adminLoginUserData?.permission_codes
        .map((code) => {
          return outsourcerAvailableMenuList[code].includes(menuName);
        })
        .includes(true);
    }
    return result;
  };

  const hasPermission = (code: OutsourcerPermissionCodes): boolean => {
    let result = false;
    if (adminUserRole && adminUserRole !== 'outsourcer') {
      result = true;
    } else if (adminUserRole === 'outsourcer' && adminLoginUserData?.permission_codes) {
      result = adminLoginUserData?.permission_codes.includes(code);
    }
    return result;
  };

  return (
    <>
      <button
        className={`${styles.side_toggle_icon_container} ${showSideMenu ? '' : styles.hides_side_menu}`}
        onClick={() => {
          setShowSideMenu(!showSideMenu);
        }}
      >
        <IoIosArrowBack className={styles.side_toggle_icon} />
      </button>
      <div className={`${styles.container} ${spMenuOpen && styles.sp_open}`}>
        <div className={`${styles.container_inner}`}>
          <div className={styles.sp_header}>
            <div className={styles.logo_wrap}>
              {!spMenuOpen ? (
                <Link href={`/console/admin/dashboard`}>
                  <a>
                    <img src="/images/iqfu/dashboard/logo.svg" alt="イクラ不動産" />
                  </a>
                </Link>
              ) : (
                <div />
              )}
              {spMenuOpen && <MdClose onClick={toggleSpMenu} />}
              {!spMenuOpen && <IoMenuSharp onClick={toggleSpMenu} />}
            </div>
          </div>
          <div className={styles.menu_container}>
            <>
              <div
                className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_iqfu} ${spMenuOpen && styles.sp_open}`}
                onClick={() => iqfuMenuToggleHandler(TargetMenu.IQFU)}
              >
                イクラ不動産
                <SideMenuArrow
                  circleFill="#fe9210"
                  className={`${styles.menuToggleIcon} ${openIqfu && styles.menuToggleIcon_isOpen}`}
                />
              </div>
              <Collapse in={openIqfu}>
                <div
                  className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_iqfu} ${
                    spMenuOpen && styles.sp_open
                  }`}
                >
                  {needsSection('dashboard') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/dashboard`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/dashboard` ? styles.current : ''
                            }`}
                          >
                            管理画面トップ
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('company') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/company`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/company` ? styles.current : ''
                            }`}
                          >
                            不動産会社
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('company_contact') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/chat/company_contact`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/chat/company_contact` ? styles.current : ''
                            }`}
                          >
                            不動産会社×運営
                            {dashboardData && dashboardData?.unread_company_inquiry_count > 0 && (
                              <img
                                src="/images/iqfu/dashboard/unread_icon.svg"
                                alt="未読"
                                className={styles.menu_unread_icon}
                              />
                            )}
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('general_company_purchase_company_chat') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/chat/general_company_purchase_company_chat`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/chat/general_company_purchase_company_chat`
                                ? styles.current
                                : ''
                            }`}
                          >
                            買取会社×仲介会社
                            {/* {dashboardData && dashboardData?.unread_company_inquiry_count > 0 && (
                            <img
                              src="/images/iqfu/dashboard/unread_icon.svg"
                              alt="未読"
                              className={styles.menu_unread_icon}
                            />
                          )} */}
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('sale_cases') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/sale_cases`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/sale_cases` ? styles.current : ''
                            }`}
                          >
                            売却実績一覧
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('seller_company_review') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/seller_company_review`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/seller_company_review` ? styles.current : ''
                            }`}
                          >
                            お客様の声
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}

                  {needsSection('purchase_agency_search') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/company/purchase_agency/search`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/company/purchase_agency/search` ? styles.current : ''
                            }`}
                          >
                            買取専門会社を探す
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('mansion') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/mansion`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/mansion` ? styles.current : ''
                            }`}
                          >
                            マンション
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('announcements') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/announcements`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/announcements` ? styles.current : ''
                            }`}
                          >
                            お知らせ
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('announcements') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/direct_announcements`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname.startsWith(`/console/admin/direct_announcements`) ? styles.current : ''
                            }`}
                          >
                            お知らせフォーム
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('city') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/city`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/city` ? styles.current : ''
                            }`}
                          >
                            市区町村
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('town') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/town`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/town` ? styles.current : ''
                            }`}
                          >
                            町域
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('contents_csv') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/contents_csv`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/contents_csv` ? styles.current : ''
                            }`}
                          >
                            コンテンツCSV投入
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('account') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/account`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/account` ? styles.current : ''
                            }`}
                          >
                            運営アカウント
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('multi_company_user') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/multi-company-user/create`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/multi-company-user/create` ? styles.current : ''
                            }`}
                          >
                            複数店舗用アカウント
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                </div>
              </Collapse>
            </>
            {adminUserRole !== 'outsourcer' && (
              <>
                <div
                  className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_csTool} ${spMenuOpen && styles.sp_open}`}
                  onClick={() => iqfuMenuToggleHandler(TargetMenu.CS_TOOL)}
                >
                  CSツール
                  <SideMenuArrow
                    circleFill="#eb5a2a"
                    className={`${styles.menuToggleIcon} ${openCsTool && styles.menuToggleIcon_isOpen}`}
                  />
                </div>
                <Collapse in={openCsTool}>
                  <div
                    className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_csTool}
  ${spMenuOpen && styles.sp_open}`}
                  >
                    {needsSection('cs_tools') && (
                      <>
                        <div className={styles.sideMenuItem}>
                          <Link href={`/console/admin/chat/search_all_chat`}>
                            <a>
                              <div
                                className={`${styles.menuText} ${
                                  router.pathname === `/console/admin/chat/search_all_chat` ? styles.current : ''
                                }`}
                              >
                                チャット検索
                              </div>
                            </a>
                          </Link>
                        </div>
                        <div className={styles.sideMenuItem}>
                          <Link href={`/console/admin/chat/search_all_memo`}>
                            <a>
                              <div
                                className={`${styles.menuText} ${
                                  router.pathname === `/console/admin/chat/search_all_memo` ? styles.current : ''
                                }`}
                              >
                                メモ検索
                              </div>
                            </a>
                          </Link>
                        </div>
                        <div className={styles.sideMenuItem}>
                          <Link href={`/console/admin/company/search_by_supported_area`}>
                            <a>
                              <div
                                className={`${styles.menuText} ${
                                  router.pathname === `/console/admin/company/search_by_supported_area`
                                    ? styles.current
                                    : ''
                                }`}
                              >
                                対応エリア別不動産会社検索
                              </div>
                            </a>
                          </Link>
                        </div>
                        <div className={styles.sideMenuItem}>
                          <Link href={`/console/admin/company/search_company_by_address`}>
                            <a>
                              <div
                                className={`${styles.menuText} ${
                                  router.pathname === `/console/admin/company/search_company_by_address`
                                    ? styles.current
                                    : ''
                                }`}
                              >
                                地図で不動産会社検索
                              </div>
                            </a>
                          </Link>
                        </div>
                        <div className={styles.sideMenuItem}>
                          <Link href={`/console/admin/chat/message_templates`}>
                            <a>
                              <div
                                className={`${styles.menuText} ${
                                  router.pathname === `/console/admin/chat/message_templates` ? styles.current : ''
                                }`}
                              >
                                チャットテンプレート
                              </div>
                            </a>
                          </Link>
                        </div>
                        <div className={styles.sideMenuItem}>
                          <Link href={`/console/admin/notion_embed_page`}>
                            <a>
                              <div
                                className={`${styles.menuText} ${
                                  router.pathname.includes(`/console/admin/notion_embed_page`) ? styles.current : ''
                                }`}
                              >
                                Notion埋め込みページ管理
                              </div>
                            </a>
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                </Collapse>
              </>
            )}
            {adminUserRole !== 'outsourcer' && (
              <>
                <div
                  className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_chat} ${spMenuOpen && styles.sp_open}`}
                  onClick={() => iqfuMenuToggleHandler(TargetMenu.CHAT)}
                >
                  売却相談チャット
                  <SideMenuArrow
                    circleFill="#17c69c"
                    className={`${styles.menuToggleIcon} ${openChat && styles.menuToggleIcon_isOpen}`}
                  />
                </div>
                <Collapse in={openChat}>
                  <div
                    className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_chat} ${
                      spMenuOpen && styles.sp_open
                    }`}
                  >
                    {needsSection('talk') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/chat/talk`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/chat/talk` ? styles.current : ''
                              }`}
                            >
                              売却相談チャット
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}

                    {needsSection('seller_contact') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/chat/seller_contact`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/chat/seller_contact` ? styles.current : ''
                              }`}
                            >
                              売主×運営
                              {dashboardData && dashboardData?.unread_seller_inquiry_count > 0 && (
                                <img
                                  src="/images/iqfu/dashboard/unread_icon.svg"
                                  alt="未読"
                                  className={styles.menu_unread_icon}
                                />
                              )}
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                    {needsSection('seller') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/chat/seller`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/chat/seller` ? styles.current : ''
                              }`}
                            >
                              売主一覧
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                    {needsSection('properties') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/chat/properties`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/chat/properties` ? styles.current : ''
                              }`}
                            >
                              相談物件一覧
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                    {needsSection('selling_consultations') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/selling_consultations`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/selling_consultations` ? styles.current : ''
                              }`}
                            >
                              売却相談一覧
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                    {needsSection('existing_seller_reports') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/chat/existing_seller_reports`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/existing_seller_reports` ? styles.current : ''
                              }`}
                            >
                              既存顧客報告一覧
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                    {needsSection('alliance_referrals') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/chat/alliance_referrals`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/chat/alliance_referrals` ? styles.current : ''
                              }`}
                            >
                              アライアンス売主一覧
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                  </div>
                </Collapse>
                <div
                  className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_leaseback} ${
                    spMenuOpen && styles.sp_open
                  }`}
                  onClick={() => iqfuMenuToggleHandler(TargetMenu.LEASEBACK)}
                >
                  リースバック
                  <SideMenuArrow
                    circleFill="#ffaec8"
                    className={`${styles.menuToggleIcon} ${openLeaseback && styles.menuToggleIcon_isOpen}`}
                  />
                </div>
                <Collapse in={openLeaseback}>
                  <div
                    className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_leaseback}
 ${spMenuOpen && styles.sp_open}`}
                  >
                    {needsSection('financial_agency_contact') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/chat/financial_agency_contact`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/chat/financial_agency_contact` ? styles.current : ''
                              }`}
                            >
                              LB事業者×運営
                              {dashboardData && dashboardData?.unread_financial_agency_division_inquiry_count > 0 && (
                                <img
                                  src="/images/iqfu/dashboard/unread_icon.svg"
                                  alt="未読"
                                  className={styles.menu_unread_icon}
                                />
                              )}
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                    {needsSection('financial_agency_company_consulting') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/chat/financial_agency_company_consulting`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/chat/financial_agency_company_consulting`
                                  ? styles.current
                                  : ''
                              }`}
                            >
                              LB事業者×不動産会社
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                    {needsSection('seller_financial_agency_consulting') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/chat/seller_financial_agency_consulting`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${styles.menuText_iqloud} ${
                                router.pathname === `/console/admin/chat/seller_financial_agency_consulting`
                                  ? styles.current
                                  : ''
                              }`}
                            >
                              LB事業者×LB売主
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                    {needsSection('referral_sellers') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/chat/referral_sellers`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname.includes(`/console/admin/chat/referral_sellers`) ? styles.current : ''
                              }`}
                            >
                              LB事業者紹介売主
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                    {needsSection('registered_leaseback_sellers') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/chat/registered_leaseback_sellers`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname.includes(`/console/admin/chat/registered_leaseback_sellers`)
                                  ? styles.current
                                  : ''
                              }`}
                            >
                              LB売主登録
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                    {needsSection('seller_company_contact') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/chat/seller_company_contact`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname.includes(`/console/admin/chat/seller_company_contact`)
                                  ? styles.current
                                  : ''
                              }`}
                            >
                              LB売主×紹介元不動産会社
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                  </div>
                </Collapse>
                <div
                  className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_deal} ${spMenuOpen && styles.sp_open}`}
                  onClick={() => iqfuMenuToggleHandler(TargetMenu.DEAL)}
                >
                  媒介・売買契約報告
                  <SideMenuArrow
                    circleFill="#a349a3"
                    className={`${styles.menuToggleIcon} ${openDeal && styles.menuToggleIcon_isOpen}`}
                  />
                </div>
                <Collapse in={openDeal}>
                  <div
                    className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_deal}
 ${spMenuOpen && styles.sp_open}`}
                  >
                    {needsSection('agreement_reports') && (
                      <>
                        <div className={styles.sideMenuItem}>
                          <Link href={`/console/admin/chat/mediation_agreement_reports`}>
                            <a>
                              <div
                                className={`${styles.menuText} ${
                                  router.pathname === `/console/admin/chat/mediation_agreement_reports`
                                    ? styles.current
                                    : ''
                                }`}
                              >
                                媒介契約報告一覧
                              </div>
                            </a>
                          </Link>
                        </div>
                        <div className={styles.sideMenuItem}>
                          <Link href={`/console/admin/chat/agreement_reports`}>
                            <a>
                              <div
                                className={`${styles.menuText} ${
                                  router.pathname === `/console/admin/chat/agreement_reports` ? styles.current : ''
                                }`}
                              >
                                売買契約報告一覧
                              </div>
                            </a>
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                </Collapse>
              </>
            )}
            {hasPermission('iqra_cloud') && (
              <>
                <div
                  className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_iqloud} ${spMenuOpen && styles.sp_open}`}
                  onClick={() => iqfuMenuToggleHandler(TargetMenu.JUSETSU)}
                >
                  重説イクラウド
                  <SideMenuArrow
                    circleFill="#0985c8"
                    className={`${styles.menuToggleIcon} ${openJusetsu && styles.menuToggleIcon_isOpen}`}
                  />
                </div>

                <Collapse in={openJusetsu}>
                  <div
                    className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_iqloud}
 ${spMenuOpen && styles.sp_open}`}
                  >
                    {needsSection('iqra_cloud/top') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/iqra_cloud`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/iqra_cloud` ? styles.current : ''
                              }`}
                            >
                              トップ
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                    {needsSection('iqra_cloud/list') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/iqra_cloud/jusetsu/list`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/iqra_cloud/jusetsu/list` ? styles.current : ''
                              }`}
                              data-test="sideMenuLink-list"
                            >
                              重説一覧
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                    {needsSection('iqra_cloud/upload') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/iqra_cloud/jusetsu/upload`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/iqra_cloud/jusetsu/upload` ? styles.current : ''
                              }`}
                              data-test="sideMenuLink-upload"
                            >
                              重説アップロード
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                    {needsSection('iqra_cloud/map') && (
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/iqra_cloud/jusetsu/map`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/iqra_cloud/jusetsu/map` ? styles.current : ''
                              }`}
                              data-test="sideMenuLink-map"
                            >
                              埋込マップ管理
                            </div>
                          </a>
                        </Link>
                      </div>
                    )}
                  </div>
                </Collapse>
              </>
            )}
            <>
              <div
                className={`${styles.sideMenuTitle} ${styles.sideMenuTitle_others} ${spMenuOpen && styles.sp_open}`}
                onClick={() => iqfuMenuToggleHandler(TargetMenu.OTHERS)}
              >
                その他
                <SideMenuArrow
                  circleFill="#99471f"
                  className={`${styles.menuToggleIcon} ${openOthers && styles.menuToggleIcon_isOpen}`}
                />
              </div>
              <Collapse in={openOthers}>
                <div
                  className={`${styles.sideMenuItemsWrapper} ${styles.sideMenuItemsWrapper_others} ${
                    spMenuOpen && styles.sp_open
                  }`}
                >
                  {needsSection('nospare_simulator') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/nospare/price_simulator`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/nospare/price_simulator` ? styles.current : ''
                            }`}
                          >
                            不動産価格相場シミュレーター
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('selling_lp_form') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/nospare/selling_lp_form`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/nospare/selling_lp_form` ? styles.current : ''
                            }`}
                          >
                            売却LP問合せフォーム
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('ai_appraisal_report') && (
                    <div className={styles.sideMenuItem}>
                      <Link href={`/console/admin/ai_appraisal_reports`}>
                        <a>
                          <div
                            className={`${styles.menuText} ${
                              router.pathname === `/console/admin/ai_appraisal_reports` ? styles.current : ''
                            }`}
                          >
                            AI査定書CSV出力
                          </div>
                        </a>
                      </Link>
                    </div>
                  )}
                  {needsSection('ai_appraisal_report') && (
                    <>
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/ai_appraisal_reports/create`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/ai_appraisal_reports/create` ? styles.current : ''
                              }`}
                            >
                              AI査定書新規作成
                            </div>
                          </a>
                        </Link>
                      </div>
                      <div className={styles.sideMenuItem}>
                        <Link href={`/console/admin/ai_appraisal_reports/list`}>
                          <a>
                            <div
                              className={`${styles.menuText} ${
                                router.pathname === `/console/admin/ai_appraisal_reports/list` ? styles.current : ''
                              }`}
                            >
                              AI査定書一覧
                            </div>
                          </a>
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </Collapse>
            </>
          </div>
        </div>
      </div>
    </>
  );
};
export default AdminSideMenu;
